/**
 * Bots against ships (the owner, Oct 20: "Bots against ships would be a good follow up"; 69.3b's ship body, DECISIONS 221:
 * with `shipsDestructible` a ship's hull takes rockets and shells, its nacelles 0.15 each, its cockpit 0.2): what a bot
 * with a rocket or a 40 mm reads of the enemy's ships — each ship low enough to be worth a round (landing, hovering,
 * unloading, parked on its pad, low on its approach; never one cruising up out of reach), the part to aim at (the nearest
 * nacelle still on, else the body), what it carries (troops or a vehicle aboard: the high-value ship), and the part where
 * the ship will be when a round gets there (its pose is a pure function of the tick, so the lead is the pose's own). Pure;
 * nothing with the switch off (the readers ask `shipsOn` first).
 */
import type { GameState, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT } from '../constants';
import { shipShapes, shipShape, parkedFrame, parkedHeliFrame, shipBodyOf, HELI_BODY } from '../core/shipBody';
import { parkedOfId, parkedHelis } from '../core/helipad';
import { flightNow, lighterNow } from '../core/shipNow';
import { lighterById } from '../core/lighter';
import { LIGHTER_FANS, LIGHTER_ID_BASE, isLighterId } from '../core/lighterBody';
import { personSetOf } from '../core/scaleProfile';
import { vecLength, vecSub } from '../math';
import type { Prim } from '../core/hits';

/** A helicopter's craft, for its body's table (core/shipBody.ts shipBodyOf). */
const HELI = { craft: 'heli' as const };

/** A part's centre (a ship's parts are boxes). */
const centreOf = (p: Prim): Vec3 => (p.cyl ? p.base : p.c);

/** An enemy ship as a bot reads it: its shape's id (a flight's id; a pad ship −1 − its team; a parked helicopter's, core/helipad.ts parkedId), its side, how high (people), what it carries, the part to aim at and where that is now. */
export interface ShipMark { id: number; team: number; heightPeople: number; aboard: boolean; part: number; at: Vec3 }

/** A ship's marked parts' health in SHIP_PARTS' order (the nacelles 0–3, the cockpit 4); absent, whole. */
function partsOf(state: GameState, id: number): number[] | undefined {
  if (isLighterId(id)) return lighterById(state, id - LIGHTER_ID_BASE)?.parts;   // 69.10 step 4
  if (id >= 0) return (state.dropships?.flights ?? []).find(f => f.id === id)?.parts;
  const parked = parkedOfId(id);   // 69.74 step 2c: a helicopter parked on its pad, its spot's
  if (parked) return state.dropships?.pads?.[parked.team]?.spots[parked.spot]?.parts;
  return state.shipPads?.[-1 - id]?.parts;
}

/**
 * The enemy ships on `planetIndex` at `tick` that `team`'s bot at `eye` could put a round on: no higher than
 * `maxHeightPeople` over the ground, each with the part to aim at — the nearest nacelle to the eye still on (two lost
 * downs a ship), else the body. In the shapes' order (flights by id, then the pads).
 */
export function enemyShips(state: GameState, team: number, planetIndex: number, tick: number, eye: Vec3, maxHeightPeople: number): ShipMark[] {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const out: ShipMark[] = [];
  for (const s of shipShapes(state, planetIndex, tick)) {
    const id = s.ref.id;
    const run = isLighterId(id) ? lighterById(state, id - LIGHTER_ID_BASE) : null;   // 69.10 step 4: a lighter, by its pose
    const f = !run && id >= 0 ? (state.dropships?.flights ?? []).find(x => x.id === id) : undefined;
    const parked = !run && id < 0 ? parkedOfId(id) : null;   // (69.74 step 2c: a parked helicopter is its side's, not a pad ship's −1 − id)
    const side = run ? run.team : f ? f.team : parked ? parked.team : -1 - id;
    if (side === team) continue;
    let heightPeople = 0, aboard = false;
    if (run) {
      const pose = lighterNow(state, run, tick)?.lighter!.pose;   // 69.17 step 1: the ship as it is now
      if (!pose) continue;
      heightPeople = pose.height / person;
      aboard = pose.aboard > 0;
    } else if (f) {
      const pose = flightNow(state, f, tick)?.flight!.pose;
      if (!pose) continue;
      heightPeople = pose.height / person;
      aboard = f.payload === 'troops' ? f.out < f.riders.length : pose.carrying;
    }
    if (heightPeople > maxHeightPeople) continue;
    const parts = partsOf(state, id);
    let part = 0, best = Infinity;
    if (run) LIGHTER_FANS.forEach((p, k) => {   // (a lighter's fans are its lifters)
      if (parts && parts[k] <= 0) return;
      const d = vecLength(vecSub(centreOf(s.prims[p]), eye));
      if (d < best) { best = d; part = p; }
    });
    else { const b = shipBodyOf(parked ? HELI : f ?? (id >= 0 ? (state.dropships?.crashes ?? []).find(x => x.id === id) : undefined)); b.table.forEach((m, k) => {   // (69.59: a helicopter's engines are its lifters, by its own table — falling too)
      if (!b.lifters.includes(m.part) || (parts && parts[k] <= 0)) return;
      const d = vecLength(vecSub(centreOf(s.prims[m.part]), eye));
      if (d < best) { best = d; part = m.part; }
    }); }
    out.push({ id, team: side, heightPeople, aboard, part, at: centreOf(s.prims[part]) });
  }
  return out;
}

/** Where part `part` of ship `id` will be at `tick` (its pose then: the lead), or null when the ship is gone by then. */
export function shipPartAt(state: GameState, id: number, part: number, tick: number): Vec3 | null {
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  if (id >= 0) {
    const f = (state.dropships?.flights ?? []).find(x => x.id === id);
    if (!f) return null;
    const now = flightNow(state, f, Math.min(tick, f.goneTick));   // (69.17 step 1: a tick ahead — a path answers it; a piloted craft will extrapolate)
    return now ? centreOf(shipShape(id, now.frame, person, null, 0).prims[part]) : null;
  }
  const parked = parkedOfId(id);
  if (parked) {   // 69.74 step 2c: a parked helicopter, on its spot (gone once it lifts or is lost)
    const h = parkedHelis(state).find(x => x.team === parked.team && x.spot === parked.spot);
    return h ? centreOf(shipShape(id, parkedHeliFrame(state, h), person, null, 0, HELI_BODY).prims[part]) : null;
  }
  const fr = parkedFrame(state, -1 - id);
  return fr ? centreOf(shipShape(id, fr, person, null, 0).prims[part]) : null;
}

/** The ship `id` as `enemyShips` reads it now (null: gone, or no longer an enemy ship in reach of a round). */
export function shipMarkNow(state: GameState, team: number, planetIndex: number, tick: number, eye: Vec3, id: number, maxHeightPeople: number): ShipMark | null {
  return enemyShips(state, team, planetIndex, tick, eye, maxHeightPeople).find(m => m.id === id) ?? null;
}
